/**
 * Structure follows `customers/columns.tsx` (same PagedTable list pattern) — no single template
 * screen mapping: `UserAdminController` is `roles-permissions`-shaped, and
 * `vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/columns.tsx` is
 * already `customers/columns.tsx`'s own source per screen-map.md — a second screen from the same
 * template file is composition, not a second copy.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Badge } from "rizzui";
import { PiEyeDuotone as EyeIcon } from "react-icons/pi";
import { AvatarCard, getStatusBadge } from "@steelyard/theme";
import { roleName } from "../../lib/format";
import { noCompanyLabel, userStatusLabel, type UserSummary } from "../../lib/services/identity";
import { UserStatusMenu } from "../../components/users/user-status-menu";
import { RowActions } from "../../components/table/row-actions";
import { userPath } from "../../lib/paths";

/** `mayChangeStatus` (`user.status`) turns the Status cell into the row's status menu; never on the caller's own row. */
export const userColumns = (mayChangeStatus = false, selfId?: string, mayActivate = false): ColumnDef<UserSummary>[] => [
  {
    id: "user",
    accessorFn: (user) => user.displayName ?? user.email ?? "Unknown user",
    header: "User",
    size: 260,
    cell: ({ row }) => (
      <Link to={userPath(row.original.userNumber || row.original.id)} className="hover:underline">
        <AvatarCard src="" name={row.original.displayName ?? row.original.email ?? "Unknown user"} description={row.original.email ?? row.original.contactEmail ?? undefined} />
      </Link>
    ),
  },
  {
    id: "role",
    accessorKey: "role",
    header: "Role",
    size: 160,
    cell: ({ row }) => (
      <Badge variant="flat" color="secondary" rounded="md" className="capitalize">
        {roleName(row.original.role)}
      </Badge>
    ),
  },
  {
    id: "company",
    // Identity sends Customers' name beside the reference; the reference itself is never shown.
    accessorFn: (user) => (user.companyRef ? (user.companyName ?? "Unknown customer") : noCompanyLabel(user)),
    header: "Company",
    size: 200,
  },
  {
    id: "status",
    accessorKey: "status",
    header: "Status",
    size: 150,
    cell: ({ row }) =>
      mayChangeStatus ? (
        <UserStatusMenu user={row.original} isSelf={row.original.id === selfId} mayActivate={mayActivate} />
      ) : (
        getStatusBadge(userStatusLabel(row.original.status))
      ),
  },
  {
    id: "locked",
    // `locked` is computed by Identity, not stored (FR-001a) — read, never re-derived here.
    accessorFn: (user) => user.locked,
    header: "Locked out",
    size: 130,
    cell: ({ row }) =>
      row.original.locked ? (
        <Badge size="sm" variant="flat" color="danger">
          Locked
        </Badge>
      ) : (
        <span className="text-muted-foreground text-xs">No</span>
      ),
  },
  {
    id: "actions",
    header: "",
    size: 90,
    cell: ({ row }) => (
      <RowActions
        name={row.original.displayName ?? row.original.email ?? "Unknown user"}
        primary={{ label: "View user", icon: EyeIcon, to: userPath(row.original.userNumber || row.original.id) }}
      />
    ),
  },
];
